import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import styles from "./index.module.css";
import { tracker } from "../../services/mixpanel";
import {
  chatbotApi,
  sendMessage,
  useGetConversationsQuery,
  useCreateConversationMutation,
  useDeleteConversationMutation,
  useGetHistoryQuery,
  useSubmitFeedbackMutation,
  useSubmitEscalationMutation,
  type HistoryItem,
} from "../../services/chatbotApi";
import { useAppDispatch } from "../../store/store";
import ChatHeader from "./ChatHeader";
import ChatSidebar from "./ChatSidebar";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import ChatFloatButton from "./ChatFloatButton";
import FeedbackModal from "./FeedbackModal";
import useDebounce from "../../hooks/useDebounce";

const SUGGESTED_PROMPTS = [
  "What is the 100-hour rule for material participation?",
  "How do solar tax credits work?",
  "What are the benefits of asset ownership?",
  "Explain bonus depreciation for solar investments",
];

// One history item → a user + bot message pair, plus any saved rating.
const mapHistory = (items: HistoryItem[]) => {
  const mapped: ChatMessage[] = [];
  const ratings: RatingMap = {};
  for (const item of items) {
    mapped.push({ id: `u-${item.id}`, text: item.question, sender: "user" });
    mapped.push({
      id: `b-${item.id}`,
      text: item.answer,
      sender: "bot",
      interactionId: item.id,
    });
    if (item.rating) ratings[item.id] = item.rating as "thumbs_up" | "thumbs_down";
  }
  return { mapped, ratings };
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [ratings, setRatings] = useState<RatingMap>({});
  const [skipHistoryFetch, setSkipHistoryFetch] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState<{
    interactionId: string;
    rating: "thumbs_up" | "thumbs_down";
  } | null>(null);
  const [escalationSubmitted, setEscalationSubmitted] = useState<Set<string>>(
    new Set()
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldApplyHistoryRef = useRef(false);
  const isStreamingRef = useRef(false);
  const hiddenDuringStreamRef = useRef(false);
  const messagesRef = useRef<ChatMessage[]>([]);

  // Set while an older history page is being fetched, so its data prepends.
  const pendingPrependRef = useRef(false);
  // Scroll metrics captured just before a prepend, to keep the viewport anchored.
  const prependInfoRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);
  // Stops scroll bursts from bumping the page before isFetching updates.
  const loadMoreLockRef = useRef(false);
  const wasFetchingConversationsRef = useRef(false);
  const historyLoadLockRef = useRef(false);
  const wasFetchingHistoryRef = useRef(false);

  const dispatch = useAppDispatch();

  const [page, setPage] = useState(1);

  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
    isFetching: isFetchingConversations,
    refetch: refetchConversations,
  } = useGetConversationsQuery(
    { search: debouncedSearch || undefined, page },
    {
      skip: !isOpen || (!!activeConversationId && !isSidebarOpen && !debouncedSearch),
    }
  );

  const conversations = conversationsData?.items ?? [];
  const hasMore = conversationsData?.hasMore ?? false;
  // Fetching a later page (data already present) vs. the initial load.
  const isFetchingMore = isFetchingConversations && !isLoadingConversations;

  const [historyPage, setHistoryPage] = useState(1);

  const { data: historyData, isError: historyError, isFetching: isFetchingHistory } = useGetHistoryQuery(
    { conversationId: activeConversationId ?? "", page: historyPage },
    { skip: !activeConversationId || skipHistoryFetch, refetchOnMountOrArgChange: true }
  );

  const historyHasMore = historyData?.hasMore ?? false;
  // Full-screen spinner for the first page; older pages keep the thread visible.
  const isLoadingInitialHistory = isFetchingHistory && historyPage === 1;
  const isLoadingOlder = isFetchingHistory && historyPage > 1;

  const [createConversation] = useCreateConversationMutation();
  const [deleteConversation] = useDeleteConversationMutation();
  const [submitFeedback] = useSubmitFeedbackMutation();
  const [submitEscalation] = useSubmitEscalationMutation();

  useEffect(() => {
    // Wait for the requested page to resolve before consuming a pending ref —
    // a new cache entry is briefly empty even while isFetching is false.
    if (isFetchingHistory || (!historyData && !historyError)) return;

    // Page 1 (conversation opened): replace the thread with recent messages.
    if (shouldApplyHistoryRef.current) {
      shouldApplyHistoryRef.current = false;
      if (historyError || !historyData?.items.length) {
        setMessages([]);
        setRatings({});
        return;
      }
      const { mapped, ratings } = mapHistory(historyData.items);
      setMessages(mapped);
      setRatings(ratings);
      return;
    }

    // Older page (scrolled to top): prepend, keeping the viewport anchored.
    if (pendingPrependRef.current) {
      pendingPrependRef.current = false;
      if (historyError || !historyData?.items.length) return;
      const { mapped, ratings } = mapHistory(historyData.items);
      const el = scrollContainerRef.current;
      prependInfoRef.current = el
        ? { prevScrollHeight: el.scrollHeight, prevScrollTop: el.scrollTop }
        : null;
      setMessages((prev) => [...mapped, ...prev]);
      setRatings((prev) => ({ ...ratings, ...prev }));
    }
  }, [historyData, historyError, isFetchingHistory]);

  // Prepending older messages: anchor the viewport before paint (no flicker).
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    const info = prependInfoRef.current;
    if (el && info) {
      el.scrollTop = info.prevScrollTop + (el.scrollHeight - info.prevScrollHeight);
    }
  }, [messages]);

  // Otherwise stick to the bottom — set it now and again next frame so late
  // content layout or the browser's scroll reset can't leave us mid-thread.
  useEffect(() => {
    if (prependInfoRef.current) {
      prependInfoRef.current = null; // prepend handled by the layout effect above
      return;
    }
    const el = scrollContainerRef.current;
    if (!el) return;
    const toBottom = () => {
      el.scrollTop = el.scrollHeight;
    };
    toBottom();
    const raf = requestAnimationFrame(toBottom);
    return () => cancelAnimationFrame(raf);
  }, [messages, isOpen, activeConversationId, isLoadingInitialHistory]);

  // Start fresh from page 1 whenever the widget or sidebar (re)opens.
  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen, isSidebarOpen]);

  // Reload page 1 (via reset if on a later page, else refetch).
  const refreshConversations = useCallback(() => {
    if (page !== 1) setPage(1);
    else refetchConversations();
  }, [page, refetchConversations]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1); // reset before the debounce fires
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isFetchingConversations || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;
    setPage((p) => p + 1);
  }, [hasMore, isFetchingConversations]);

  // Release the lock on the falling edge of isFetching (page settled).
  useEffect(() => {
    if (wasFetchingConversationsRef.current && !isFetchingConversations) {
      loadMoreLockRef.current = false;
    }
    wasFetchingConversationsRef.current = isFetchingConversations;
  }, [isFetchingConversations]);

  const handleLoadOlder = useCallback(() => {
    if (!historyHasMore || isFetchingHistory || historyLoadLockRef.current) return;
    historyLoadLockRef.current = true;
    pendingPrependRef.current = true;
    setHistoryPage((p) => p + 1);
  }, [historyHasMore, isFetchingHistory]);

  useEffect(() => {
    if (wasFetchingHistoryRef.current && !isFetchingHistory) {
      historyLoadLockRef.current = false;
    }
    wasFetchingHistoryRef.current = isFetchingHistory;
  }, [isFetchingHistory]);

  useEffect(() => {
    tracker.chatbot.appeared();
  }, []);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // The browser kills the streaming connection when the page goes to standby.
  // Remember that it happened mid-stream so the failure can be recovered from
  // history instead of surfacing as an error.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && isStreamingRef.current) {
        hiddenDuringStreamRef.current = true;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const toggleChat = useCallback(() => {
    if (!isOpen) {
      tracker.chatbot.opened();
      openedAtRef.current = Date.now();
      setIsOpen(true);
    } else {
      const secs = openedAtRef.current
        ? Math.round((Date.now() - openedAtRef.current) / 1000)
        : 0;
      tracker.chatbot.closed(secs);
      openedAtRef.current = null;
      abortRef.current?.abort();
      setIsOpen(false);
    }
  }, [isOpen]);

  const handleNewChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setInputMessage("");
    setIsStreaming(false);
    setActiveConversationId(null);
    setSearchQuery("");
    setPage(1);
    setHistoryPage(1);
    setRatings({});
    setEscalationSubmitted(new Set());
    setSkipHistoryFetch(false);
    shouldApplyHistoryRef.current = false;
    pendingPrependRef.current = false;
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSelectConversation = useCallback(
    (id: string) => {
      if (id === activeConversationId || isStreaming) return;
      abortRef.current?.abort();
      setSkipHistoryFetch(false);
      setActiveConversationId(id);
      setMessages([]);
      setRatings({});
      setEscalationSubmitted(new Set());
      setHistoryPage(1);
      shouldApplyHistoryRef.current = true;
      pendingPrependRef.current = false;
      prependInfoRef.current = null;
    },
    [activeConversationId, isStreaming]
  );

  const handleDeleteConversation = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      try {
        await deleteConversation(id).unwrap();
        // Drop it from the cache in place (page arg only sets the search key).
        dispatch(
          chatbotApi.util.updateQueryData(
            "getConversations",
            { search: debouncedSearch || undefined, page: 1 },
            (draft) => {
              draft.items = draft.items.filter((c) => c.id !== id);
            }
          )
        );
        if (activeConversationId === id) handleNewChat();
      } catch {
        // ignore
      }
    },
    [deleteConversation, dispatch, debouncedSearch, activeConversationId, handleNewChat]
  );

  // After a standby/resume the stream is dead but the backend keeps generating
  // and saves the answer. Poll history until the answer for this question
  // appears, then swap it into the placeholder bot message.
  const recoverInterruptedAnswer = useCallback(
    async (convoId: string, question: string, botMsgId: string) => {
      // Interactions already on screen must not be mistaken for the answer we
      // are waiting on (e.g. the same question asked twice in a conversation).
      const displayedIds = new Set(
        messagesRef.current
          .map((m) => m.interactionId)
          .filter((id): id is string => Boolean(id))
      );
      // Generation can run well past a minute server-side; poll ~70s total.
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise((resolve) =>
          setTimeout(resolve, attempt < 5 ? 2000 : 4000)
        );
        try {
          const sub = dispatch(
            chatbotApi.endpoints.getHistory.initiate(
              { conversationId: convoId, page: 1 },
              { forceRefetch: true }
            )
          );
          const historyRes = await sub.unwrap();
          sub.unsubscribe();
          const history = historyRes.items;
          const match = [...history]
            .reverse()
            .find(
              (item) =>
                item.question === question &&
                !!item.answer &&
                !displayedIds.has(item.id)
            );
          if (match) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botMsgId
                  ? { ...m, text: match.answer, interactionId: match.id }
                  : m
              )
            );
            setIsStreaming(false);
            refreshConversations();
            setTimeout(() => inputRef.current?.focus(), 0);
            return;
          }
        } catch {
          // keep polling
        }
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === botMsgId
            ? {
                ...m,
                text: "The connection was interrupted. Please try again.",
              }
            : m
        )
      );
      setIsStreaming(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [dispatch, refreshConversations]
  );

  const sendMessageText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      const userMsgId = crypto.randomUUID();
      const botMsgId = crypto.randomUUID();

      setMessages((prev) => [
        ...prev,
        { id: userMsgId, text: trimmed, sender: "user" },
        { id: botMsgId, text: "", sender: "bot" },
      ]);
      setInputMessage("");
      setIsStreaming(true);
      hiddenDuringStreamRef.current = false;

      const abort = new AbortController();
      abortRef.current = abort;

      let convoId = activeConversationId;
      let finished = false;

      try {
        const isFirstChat = !convoId;
        if (!convoId) {
          const convo = await createConversation().unwrap();
          convoId = convo.id;
          setSkipHistoryFetch(true);
          setActiveConversationId(convo.id);
        }

        await sendMessage(
          trimmed,
          convoId,
          {
            onDelta: (deltaText) =>
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botMsgId ? { ...m, text: m.text + deltaText } : m
                )
              ),
            onDone: (interactionId) => {
              finished = true;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botMsgId ? { ...m, interactionId } : m
                )
              );
              setIsStreaming(false);
              setTimeout(() => inputRef.current?.focus(), 0);

              // Surface the new conversation (server-titled) at the top.
              if (isFirstChat) refreshConversations();
            },
            onError: () => {
              finished = true;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === botMsgId
                    ? {
                        ...m,
                        text: "Sorry, I encountered an error. Please try again.",
                      }
                    : m
                )
              );
              setIsStreaming(false);
              setTimeout(() => inputRef.current?.focus(), 0);
            },
          },
          abort.signal
        );

        // Stream ended without a "done" event: the connection was severed
        // (e.g. page standby). The backend keeps generating and saves the
        // answer, so recover it from history instead of erroring.
        if (!finished) {
          await recoverInterruptedAnswer(convoId, trimmed, botMsgId);
        }
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        if (hiddenDuringStreamRef.current && convoId) {
          await recoverInterruptedAnswer(convoId, trimmed, botMsgId);
          return;
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botMsgId
              ? {
                  ...m,
                  text: "Couldn't connect to the assistant. Please try again.",
                }
              : m
          )
        );
        setIsStreaming(false);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    },
    [
      isStreaming,
      activeConversationId,
      createConversation,
      refreshConversations,
      recoverInterruptedAnswer,
    ]
  );

  const handleSend = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      sendMessageText(inputMessage);
    },
    [inputMessage, sendMessageText]
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.sender === "bot" && !last.text) return prev.slice(0, -1);
      return prev;
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleFeedbackClick = useCallback(
    (interactionId: string, rating: "thumbs_up" | "thumbs_down") => {
      if (ratings[interactionId]) return;
      setFeedbackModal({ interactionId, rating });
    },
    [ratings]
  );

  const handleFeedbackSubmit = useCallback(
    async (feedbackText: string) => {
      if (!feedbackModal) return;
      const { interactionId, rating } = feedbackModal;
      setRatings((prev) => ({ ...prev, [interactionId]: rating }));
      setFeedbackModal(null);
      try {
        await submitFeedback({
          interactionId,
          rating,
          feedbackText: feedbackText.trim() || null,
        }).unwrap();
      } catch {
        setRatings((prev) => {
          const next = { ...prev };
          delete next[interactionId];
          return next;
        });
      }
    },
    [feedbackModal, submitFeedback]
  );

  const handleFeedbackClose = useCallback(() => {
    handleFeedbackSubmit("");
  }, [handleFeedbackSubmit]);

  const handleEscalationSubmit = useCallback(
    async (
      messageId: string,
      name: string,
      email: string,
      question: string
    ) => {
      try {
        await submitEscalation({
          name: name.trim(),
          email: email.trim(),
          question,
          conversationId: activeConversationId,
        }).unwrap();
        setEscalationSubmitted((prev) => {
          const next = new Set(prev);
          next.add(messageId);
          return next;
        });
        return true;
      } catch {
        return false;
      }
    },
    [submitEscalation, activeConversationId]
  );

  const toggleSidebar = useCallback(() => setIsSidebarOpen((v) => !v), []);

  return (
    <div className="fixed bottom-20 right-6 z-[9999] flex flex-col items-end font-sans">
      {isOpen && (
        <div
          className={`relative bg-white rounded-2xl shadow-2xl flex flex-col mb-4 overflow-hidden border border-gray-200 transition-all duration-300 ease-in-out h-[600px] ${
            isSidebarOpen ? "w-[720px] sm:w-[780px]" : "w-[420px] sm:w-[460px]"
          } ${styles.chatWindow}`}
        >
          <div className="flex flex-1 overflow-hidden">
            <ChatSidebar
              isOpen={isSidebarOpen}
              conversations={conversations}
              isLoadingConversations={isLoadingConversations}
              isFetchingMore={isFetchingMore}
              hasMore={hasMore}
              activeConversationId={activeConversationId}
              searchQuery={searchQuery}
              onNewChat={handleNewChat}
              onSearchChange={handleSearchChange}
              onSelectConversation={handleSelectConversation}
              onDeleteConversation={handleDeleteConversation}
              onLoadMore={handleLoadMore}
            />

            <div className="flex flex-col flex-1 overflow-hidden">
              <ChatHeader
                isSidebarOpen={isSidebarOpen}
                onToggleSidebar={toggleSidebar}
                onClose={toggleChat}
              />
              <ChatMessages
                messages={messages}
                isLoadingHistory={isLoadingInitialHistory}
                hasMoreHistory={historyHasMore}
                isLoadingOlder={isLoadingOlder}
                activeConversationId={activeConversationId}
                ratings={ratings}
                suggestedPrompts={SUGGESTED_PROMPTS}
                escalationSubmitted={escalationSubmitted}
                onFeedback={handleFeedbackClick}
                onPromptClick={sendMessageText}
                onEscalationSubmit={handleEscalationSubmit}
                onLoadOlder={handleLoadOlder}
                scrollContainerRef={scrollContainerRef}
              />
              <ChatInput
                inputMessage={inputMessage}
                isStreaming={isStreaming}
                inputRef={inputRef}
                onInputChange={setInputMessage}
                onSubmit={handleSend}
                onStop={handleStop}
              />
            </div>
          </div>

          {feedbackModal && (
            <FeedbackModal
              rating={feedbackModal.rating}
              onSubmit={handleFeedbackSubmit}
              onClose={handleFeedbackClose}
            />
          )}
        </div>
      )}

      <ChatFloatButton isOpen={isOpen} onClick={toggleChat} />
    </div>
  );
};

export default ChatBot;
